import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { SelectGroup, SelectItem } from '@cko/primitives';
import { getAllTimeZones, UTCTimeZone, getDefaultTimeZone } from '../../../utils';
import { useCurrentClientManager, useUserLevelSettings } from '../../../context';
import { SearchableSelect } from './TimeZoneCombobox.styles';
import { TimeZoneOffset } from './TimeZoneOffset';
export const TimeZoneCombobox = ({ isOpenByDefault = false, onChange, shouldLabelDisplay = false, }) => {
    const { availableEntities } = useCurrentClientManager();
    const defaultTimezome = useMemo(() => getDefaultTimeZone(), []);
    const timeZones = useMemo(() => getAllTimeZones(), []);
    const { selectedTimeZone } = useUserLevelSettings();
    const componentRef = useRef(null);
    const onUpdate = useCallback((option) => {
        onChange(option);
    }, [onChange]);
    const availableEntityTimeZones = useMemo(() => {
        if (!availableEntities) {
            return [];
        }
        const entityZones = availableEntities
            ?.map((entity) => timeZones.find((zone) => zone.value === entity.timeZone))
            .filter((zone) => zone !== undefined);
        // If no entities have an associated time zone, fall back to utc
        if (entityZones.length < 1) {
            return [UTCTimeZone];
        }
        return entityZones.reduce((acc, cur) => {
            const included = acc.some((zone) => zone.value === cur.value);
            if (included) {
                return acc;
            }
            return [...acc, cur];
        }, []);
        // Do not include timeZones in the deps list as this will not update
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableEntities]);
    useEffect(() => {
        const tzSelect = document.getElementById('trigger-container')?.getElementsByTagName('input');
        // this needs to work differently in the User Profile page
        if (tzSelect && isOpenByDefault) {
            tzSelect[0].click();
            tzSelect[0].focus();
        }
    }, [isOpenByDefault]);
    //select group cant be optionally rendered so we have to render two versions of the group, one with settlement timezone if available, one without
    return (React.createElement("div", { ref: componentRef },
        React.createElement(SearchableSelect, { label: "Time zone", key: selectedTimeZone.value, shouldLabelDisplay: shouldLabelDisplay, isRequired: true, placeholder: "Search time zones", onChange: onUpdate, showClear: false, initialSelected: isOpenByDefault
                ? undefined
                : {
                    label: selectedTimeZone.label,
                    value: selectedTimeZone.value,
                    prefix: React.createElement(TimeZoneOffset, { utcOffsetString: selectedTimeZone.utcOffsetString }),
                } },
            React.createElement(SelectGroup, { label: `Settlement time ${availableEntityTimeZones.length === 1 ? 'zone' : 'zones'}` }, availableEntityTimeZones.filter(Boolean).map((zone) => (React.createElement(SelectItem, { key: zone.value, "data-testid": "settlement-time-zone", label: zone.label, prefix: React.createElement(TimeZoneOffset, { utcOffsetString: zone.utcOffsetString }), value: zone.value })))),
            React.createElement(SelectGroup, { label: "Browser default time zone" },
                React.createElement(SelectItem, { "data-testid": "browser-default-timezone", label: defaultTimezome.label, prefix: React.createElement(TimeZoneOffset, { utcOffsetString: defaultTimezome.utcOffsetString }), value: defaultTimezome.value })),
            React.createElement(SelectGroup, { label: "Time zones" }, timeZones.map((zone) => (React.createElement(SelectItem, { key: zone.value, label: zone.label, prefix: React.createElement(TimeZoneOffset, { utcOffsetString: zone?.utcOffsetString || '+1' }), value: zone.value })))))));
};
